import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import {
  Zap, Fuel, Settings, Gauge, Clock, Award, Shield, Star,
  UserPlus, Upload, Download, FileText, Headphones, ShieldCheck, BadgeCheck,
  CheckCircle2, AlertTriangle
} from 'lucide-react';
import { services, advantages, faqs, stats, howItWorks, guarantees } from '../mockData';

const iconMap = {
  zap: Zap, fuel: Fuel, settings: Settings, gauge: Gauge,
  clock: Clock, award: Award, shield: Shield, star: Star,
  'user-plus': UserPlus, upload: Upload, download: Download,
  'file-text': FileText, headphones: Headphones,
  'shield-check': ShieldCheck, 'badge-check': BadgeCheck
};

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#111111] text-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#1a1a1a] to-[#111111]"></div>
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#C00000]/10 to-transparent"></div>

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-[#C00000]/10 border border-[#C00000]/30 rounded-full">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C00000] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C00000]"></span>
              </span>
              <span className="text-[#C00000] font-semibold tracking-wide text-sm" data-testid="engineers-online-badge">
                Ingénieurs en ligne
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              FICHIERS DE REPROGRAMMATION ECU<br />
              <span className="text-[#C00000]">SUR MESURE</span> POUR LES PROFESSIONNELS
            </h1>

            <p className="text-lg md:text-xl text-[#A6A6A6] mb-12 max-w-4xl mx-auto">
              Plateforme réservée aux préparateurs équipés de leur propre matériel ECU.
              Stage 1, 2, 3, E85, FAP/EGR/Adblue. Fichiers livrés en moins de 20 minutes.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <Button
                onClick={() => navigate('/register')}
                data-testid="hero-cta-register"
                className="bg-[#C00000] hover:bg-[#C00000]/90 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-[#C00000]/40 transition-all hover:scale-105">
                Commencer maintenant
                <Zap className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={() => navigate('/login')}
                data-testid="hero-cta-login"
                variant="outline"
                className="border-2 border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10 font-semibold px-8 py-6 text-lg transition-all hover:scale-105">
                Déjà client ? Connexion
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-3xl mx-auto" data-testid="hero-stats">
              {stats.map((stat, i) => (
                <div key={i} className="bg-[#1a1a1a]/60 backdrop-blur border border-[#A6A6A6]/10 rounded-xl p-4 md:p-6">
                  <div className="text-2xl md:text-4xl font-bold text-[#C00000] mb-1">{stat.value}</div>
                  <div className="text-xs md:text-sm text-[#A6A6A6] uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-20 px-6 bg-[#0a0a0a]" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              COMMENT <span className="text-[#C00000]">ÇA MARCHE</span> ?
            </h2>
            <p className="text-[#A6A6A6] text-lg">3 étapes simples pour recevoir votre fichier optimisé</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {howItWorks.map((step) => {
              const Icon = iconMap[step.icon];
              return (
                <div key={step.step} className="relative bg-[#1a1a1a] border border-[#A6A6A6]/20 rounded-2xl p-8 hover:border-[#C00000]/50 transition-all group">
                  <div className="absolute -top-5 -left-5 w-12 h-12 bg-[#C00000] text-white text-2xl font-bold rounded-full flex items-center justify-center shadow-lg shadow-[#C00000]/40">
                    {step.step}
                  </div>
                  <div className="mb-4 inline-block p-3 bg-[#C00000]/10 rounded-lg group-hover:bg-[#C00000]/20 transition-colors">
                    <Icon className="h-8 w-8 text-[#C00000]" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-white">{step.title}</h3>
                  <p className="text-[#A6A6A6] text-sm leading-relaxed">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Garanties pro */}
      <section className="py-12 px-6 border-y border-[#A6A6A6]/10" data-testid="guarantees-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {guarantees.map((g, i) => {
              const Icon = iconMap[g.icon];
              return (
                <div key={i} className="text-center">
                  <div className="mb-3 inline-block p-3 bg-[#C00000]/10 rounded-full">
                    <Icon className="h-6 w-6 text-[#C00000]" />
                  </div>
                  <h4 className="font-bold text-white text-sm mb-1">{g.title}</h4>
                  <p className="text-[#A6A6A6] text-xs">{g.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#111111] to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              NOS <span className="text-[#C00000]">SERVICES</span>
            </h2>
            <p className="text-[#A6A6A6] text-lg">
              Des prestations professionnelles pour tous types de véhicules : voitures, motos, camions, tracteurs, bateaux.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="services-grid">
            {services.map((service) => {
              const Icon = iconMap[service.icon];
              return (
                <Card key={service.id} className="bg-[#1a1a1a] border-[#A6A6A6]/20 hover:border-[#C00000]/50 transition-all duration-300 group hover:scale-105">
                  <CardContent className="p-6">
                    <div className="mb-4 inline-block p-3 bg-[#C00000]/10 rounded-lg group-hover:bg-[#C00000]/20 transition-colors">
                      <Icon className="h-8 w-8 text-[#C00000]" />
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-white">{service.title}</h3>
                    <p className="text-[#A6A6A6] mb-4 text-sm leading-relaxed">{service.description}</p>
                    <p className="text-[#C00000] font-bold text-lg">{service.price}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              POURQUOI <span className="text-[#C00000]">NOUS CHOISIR</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage) => {
              const Icon = iconMap[advantage.icon];
              return (
                <div key={advantage.id} className="text-center group">
                  <div className="mb-4 inline-block p-4 bg-gradient-to-br from-[#C00000]/20 to-transparent rounded-full group-hover:scale-110 transition-transform">
                    <Icon className="h-10 w-10 text-[#C00000]" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{advantage.title}</h3>
                  <p className="text-[#A6A6A6]">{advantage.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Espace Professionnel - Avertissement */}
      <section className="py-20 px-6 bg-gradient-to-r from-[#1a1a1a] via-[#0a0a0a] to-[#1a1a1a]" data-testid="pro-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              ESPACE <span className="text-[#C00000]">PROFESSIONNEL</span>
            </h2>
            <p className="text-[#A6A6A6] text-lg">Réservé aux professionnels de la reprogrammation</p>
          </div>

          <div className="bg-[#C00000]/5 border border-[#C00000]/30 rounded-2xl p-6 md:p-8 mb-8">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-[#C00000]/20 rounded-lg flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-[#C00000]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Accès réservé aux professionnels</h3>
                <p className="text-[#A6A6A6] leading-relaxed">
                  Cette plateforme est destinée exclusivement aux professionnels de la reprogrammation moteur disposant
                  de leur propre matériel de lecture/écriture ECU (Alientech KESS3, Autotuner, Bflash, etc.).
                  Vous devez posséder un outil de diagnostic et maîtriser la reprogrammation.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
            {[
              "Catalogue véhicules complet (auto, moto, camion, tracteur, marine)",
              "Compatible KESS3, Autotuner, Bflash, BitBox, CMD, DimSport...",
              "Master & Slave, lecture OBD / Bench / Boot / BDM",
              "Suivi en temps réel de vos commandes & chat ingénieur",
              "Multi-versions de fichiers (V1, V2, V3...)",
              "Factures et historique disponibles dans votre espace"
            ].map((feat, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#1a1a1a]/60 border border-[#A6A6A6]/10 rounded-lg p-4">
                <CheckCircle2 className="h-5 w-5 text-[#C00000] flex-shrink-0 mt-0.5" />
                <span className="text-white text-sm">{feat}</span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button
              onClick={() => navigate('/register')}
              data-testid="pro-cta-register"
              className="bg-[#C00000] hover:bg-[#C00000]/90 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-[#C00000]/40 transition-all hover:scale-105">
              Créer mon compte gratuit
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              QUESTIONS <span className="text-[#C00000]">FRÉQUENTES</span>
            </h2>
            <p className="text-[#A6A6A6] text-lg">
              Tout ce que vous devez savoir sur notre file service de reprogrammation ECU
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4" data-testid="faq-accordion">
            {faqs.map((faq) => (
              <AccordionItem key={faq.id} value={`item-${faq.id}`} className="bg-[#1a1a1a] border border-[#A6A6A6]/20 rounded-lg px-6">
                <AccordionTrigger className="text-white hover:text-[#C00000] text-left font-semibold">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#A6A6A6] leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#0a0a0a] border-t border-[#A6A6A6]/20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <span className="text-2xl font-bold">
                <span className="text-[#A6A6A6]">GT</span>
                <span className="text-[#C00000]"> ENGINEERING</span>
              </span>
              <p className="text-sm text-[#A6A6A6] mt-2">Engineered Performance.</p>
              <p className="text-xs text-[#A6A6A6] mt-4 leading-relaxed">
                File service professionnel de reprogrammation ECU pour préparateurs et professionnels automobiles.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-3">Services</h4>
              <ul className="space-y-2 text-sm text-[#A6A6A6]">
                <li>Stage 1, 2 & 3</li>
                <li>Conversion E85</li>
                <li>FAP / EGR / Adblue</li>
                <li>Options Performance</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-3">Horaires</h4>
              <ul className="space-y-2 text-sm text-[#A6A6A6]">
                <li>Lundi - Samedi</li>
                <li>9h00 - 19h30</li>
                <li className="text-[#C00000] font-semibold">Fichiers en &lt;20 min</li>
              </ul>
            </div>
          </div>
          <div className="text-center pt-6 border-t border-[#A6A6A6]/10">
            <p className="text-[#A6A6A6] text-sm">
              © 2025 GT Engineering. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
